import * as Sentry from "@sentry/browser";
import React, {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Auth0Provider,
  GetTokenSilentlyOptions,
  useAuth0,
} from "@auth0/auth0-react";
import { Text } from "./text";
import { useQueryString } from "../hooks/use_query_string";
import { useAnalytics } from "@/lib/analytics/analytics";
import { useHistory, useLocation, useParams } from "react-router-dom";

interface AuthPortalProviderProps {
  children: React.ReactNode;
  domain: string;
  clientId: string;
  audience: string;
  redirectUri: string;
}

export function AuthPortalProvider(props: AuthPortalProviderProps) {
  const { children, domain, clientId, audience, redirectUri } = props;

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      audience={audience}
      redirectUri={redirectUri}
    >
      <AuthProvider>{children}</AuthProvider>
    </Auth0Provider>
  );
}

interface AuthContext {
  token: string;
  user: any;
  setToken: React.Dispatch<React.SetStateAction<string>>;
  getTokenSilently: (options?: GetTokenSilentlyOptions) => Promise<any>;
}

const AuthContext = createContext<AuthContext>({
  token: "",
  user: null,
  setToken: () => {},
  getTokenSilently: () => Promise.resolve(),
});

interface AuthProviderProps {
  children: React.ReactNode;
}

interface CheckoutAuthParams {
  email: string;
  screen: "login" | "signup";
  partnerSlug: string;
}

export function useAuth() {
  return useContext(AuthContext);
}

function AuthProvider(props: AuthProviderProps) {
  const { children } = props;
  const { loading, authenticated } = usePartnerPortalAuth();
  const { user, isLoading, getAccessTokenSilently } = useAuth0();
  const [token, setToken] = useState("");
  const analytics = useAnalytics();

  useEffect(() => {
    if (!user) {
      return;
    }

    Sentry.setUser({ id: user.sub, email: user.email });
    analytics.identify(user.sub || "", {
      name: user.name,
      email: user.email,
      phone: user.phone_number,
      source: "Portal",
    });
  }, [analytics, user]);
  useEffect(() => {
    if (!isLoading) {
      getAccessTokenSilently()
        .then((t) => {
          setToken(t);
        })
        .catch((e) => {
          console.error("Unauthenticated", e);
        });
    }
  }, [isLoading, getAccessTokenSilently]);

  if (loading || !token) {
    return <Fragment />;
  }

  if (!authenticated) {
    return <Text>Redirecting you to login page...</Text>;
  }

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        setToken,
        getTokenSilently: getAccessTokenSilently,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function usePartnerPortalAuth() {
  const { isAuthenticated, loginWithRedirect, user, isLoading } = useAuth0();
  const location = useLocation();
  const queryString = useQueryString<CheckoutAuthParams>();

  const history = useHistory();
  const partnerSlug = useParams<{ partnerSlug: string }>().partnerSlug;

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated || !user) {
      if (
        location.pathname !== "/portal/auth/login" &&
        location.pathname !== "/portal/auth/signup" &&
        location.pathname !== "/portal/auth/callback"
      ) {
        const targetPath = queryString.screen === "signup" ? "signup" : "login";
        history.replace(
          `/portal/auth/${targetPath}?partnerSlug=${
            queryString.partnerSlug || partnerSlug
          }${queryString.email ? `&email=${queryString.email}` : ""}`
        );
      }
    }
  }, [
    isLoading,
    isAuthenticated,
    user,
    history,
    partnerSlug,
    loginWithRedirect,
    queryString,
    location.pathname,
  ]);

  return {
    loading: isLoading,
    authenticated: isAuthenticated,
  };
}
